import { defineStore } from 'pinia'
import useCustomFetch from '~/composables/useCustomFetch'
import { STATUS_OK } from '~/constants/apiStatus'
import { useUserStore, useModalStore } from '~/stores'
import { PROVIDERS } from '~/constants/providers'
import _ from 'lodash'

const apiProviderAdapters = {
    ['tables']: 'table_game',
    ['slot']: 'slots',
    ['fishing']: 'fishing',
    ['virtual_sport']: 'virtual_sport',
    ['lotto']: 'lottery',
    ['quick_game']: 'quick_game',
    ['cock_fight']: 'cockfight',
    ['instants']: 'instant',
    ['lode']: 'lottery',
    ['']: 'all',
}

const commonAll = {
    id: '',
    key: '',
    name: 'provider-all',
}

const typeMapping = {
    cockfight: [],
    tables: 'table_game',
    instants: 'instant',
    lode: 'lottery',
    '': 'all',
}

function propertiesMapping(item, type) {
    let link = PROVIDERS[item.key]?.link || ''
    let image = PROVIDERS[item.key]?.image || ''

    const typeToLinkAndImage = {
        fishing: {
            link: `/games/fishing?partner=${PROVIDERS[item.key]?.id}`,
            image: `/assets/icons/casino/provider/fishing/${PROVIDERS[item.key]?.id}.png`,
        },
        table_game: {
            link: `/games/tables?partner=${PROVIDERS[item.key]?.id}`,
            image: `/assets/icons/casino/provider/table-game/${PROVIDERS[item.key]?.id}.png`,
        },
        slots: {
            link: `/games/slots?partner=${PROVIDERS[item.key]?.id}`,
            image: PROVIDERS[item.key].image,
        },
        lotto: {
            link: `/games/lottery?partner=${PROVIDERS[item.key]?.id}`,
            image: `/assets/icons/casino/provider/lottery/${PROVIDERS[item.key]?.id}.png`,
        },
        quick_game: {
            link: `/games/instants?partner=${PROVIDERS[item.key]?.id}`,
            image: `/assets/img/games/q_mb_${PROVIDERS[item.key]?.id}.png`,
        },
        live_casino: {
            link: `casino/all?partner=${PROVIDERS[item.key]?.id}`,
            image: `/assets/icons/casino/provider/live-casino/${PROVIDERS[item.key]?.id}.png`,
        },
    }

    if (typeToLinkAndImage[type]) {
        link = typeToLinkAndImage[type].link
        image = typeToLinkAndImage[type].image
    }
    return { link, image }
}

export const useGamesStore = defineStore('gamesStore', () => {
    const fetcher = useCustomFetch()
    const useUserStoreInstance = useUserStore()
    const useModalStoreInstance = useModalStore()
    const { user, isLoggedIn } = storeToRefs(useUserStoreInstance)

    const { showUpdateFullnameModal, showGameDenyModal, showLoginModal } = storeToRefs(useModalStoreInstance)

    const recommendedGames = ref([])
    const providersByType = ref({})
    const gameTypes = ref([])
    const games = ref([])
    const hotSlotGames = ref([])
    const usersLive = ref({
        'b52-gamels': [],
        'go-gamels': [],
    })

    const initProviders = async () => {
        try {
            const { data, error } = await fetcher.get('/game/provider')
            if (data.value.status !== 'OK') {
                throw error
            }
            const responseData = data?.value?.data
            // console.log('game provider', Object.entries(responseData))
            for (const [type, providers] of Object.entries(responseData)) {
                providersByType.value[apiProviderAdapters[type] || type] =
                    providers
                        .map((item) => {
                            if (PROVIDERS[item.key]?.id === undefined) {
                                return
                            }

                            let { link, image } = propertiesMapping(item, type)

                            return {
                                ...PROVIDERS[item.key],
                                link,
                                image,
                                label: item?.label,
                            }
                        })
                        .filter((item) => !!item)
            }
        } catch (err) {
            console.log('providerStore', err)
        }
    }

    const getGameProviders = async () => {
        try {
            if (!_.isEmpty(providersByType.value)) return

            const { data, error } = await fetcher.get('/game/provider')
            if (data.value.status !== 'OK') {
                throw error
            }
            const responseData = data?.value?.data
            // console.log('game provider', Object.entries(responseData))
            for (const [type, providers] of Object.entries(responseData)) {
                providersByType.value[apiProviderAdapters[type] || type] =
                    providers.map((item) => {
                        return {
                            id: item.key.toLowerCase(),
                            ...item,
                        }
                    })
            }
        } catch (err) {
            console.log('providerStore', err)
        }
    }

    const getGameTypes = async () => {
        try {
            // if (!_.isEmpty(gameTypes.value)) return

            const { data } = await fetcher.get('/game/types')
            const responseData = data?.value?.data
            // console.log('getGameTypes', responseData)
            gameTypes.value = responseData.map((item) => {
                return {
                    id: item.key,
                    ...item,
                }
            })
        } catch (error) {
            console.log('getGameTypes', error)
        }
    }

    function getGamesProvidersByType(type) {
        const key = typeMapping[type] || type

        return providersByType.value[key]
    }

    function getGamesProvidersAllByType(type) {
        const key = typeMapping[type] || type

        return [commonAll, ...(providersByType.value[key] || [])]
    }

    const getGames = async (queryParams) => {
        try {
            const { data, pending, error } = await fetcher.get(
                '/game/search/',
                { ...queryParams, limit: 20 },
                20 * 1000
            )

            if (data.value?.status === STATUS_OK) {
                if (queryParams.page > 1) {
                    games.value.items.push(...data.value.data.items)
                } else {
                    games.value = data.value.data
                }
            }
            return { data: games, pending, error }
        } catch (error) {
            console.log('gamesStore', error)
        }
    }

    const getHotSlotGames = async () => {
        try {
            const { data } = await fetcher.get(
                '/game/search/?type=slots&filter=hot&keyword&page=1&limit=5'
            )

            if (data.value?.status === STATUS_OK) {
                hotSlotGames.value = data.value.data
                // console.log('hotSlotGames', data.value.data)
            }
        } catch (error) {
            console.log('getHotSlotGames', error)
        }
    }

    const getGameUrl = async (queryParams) => {
        if (isLoggedIn.value) {
            const gameProviders = ['rik', 'b52', 'go', 'iwin', 'win79']
            if (
                gameProviders.includes(
                    queryParams.partnerProvider?.toLowerCase()
                ) &&
                (!user.value.fullname ||
                    user.value.fullname === user.value.username)
            ) {
                showUpdateFullnameModal.value = true
                return {
                    data: ref(null),
                }
            }

            const { data } = await fetcher.get('/gameUrl', queryParams)
            if (data.value?.status === 'SHOW_MESSAGE') {
                showGameDenyModal.value = true
                return { data: ref(null) }
            } else {
                return { data: data }
            }
        } else {
            showLoginModal.value = true
            // return an empty ref
            return { data: ref(null) }
        }
    }

    const getGameUrlNoAuth = async (queryParams) => {
        const { data } = await fetcher.get('/gameUrl', queryParams)
        if (data.value?.status === 'SHOW_MESSAGE') {
            showGameDenyModal.value = true
            return { data: ref(null) }
        } else {
            return { data: data }
        }
    }

    const getRecommendedGames = async (limit) => {
        try {
            const { data } = await fetcher.get(
                `/game/search?type=&filter=suggest&page=1&limit=${limit}`
            )

            if (data.value?.status === STATUS_OK) {
                recommendedGames.value = data.value.data.items
            } else {
                console.log('recommendedGames error', data.value)
            }
        } catch (error) {
            console.log('getRecommendedGames', error)
        }
    }

    const updateUsersLive = (channel, data) => {
        if (channel && data) {
            const { activeUsers, gameId, mapGameId } = data

            const indexGame = usersLive.value[channel].findIndex(
                (game) => game.gameId === gameId && game.mapGameId == mapGameId
            )
            if (indexGame > -1) {
                usersLive.value[channel][indexGame] = {
                    activeUsers,
                    gameId,
                    mapGameId,
                }
            } else {
                usersLive.value[channel].push({
                    activeUsers,
                    gameId,
                    mapGameId,
                })
            }
        }
    }

    return {
        initProviders,
        usersLive,
        providersByType,
        getGamesProvidersByType,
        getGamesProvidersAllByType,
        getGames,
        getGameUrl,
        getGameUrlNoAuth,
        getRecommendedGames,
        recommendedGames,
        games,
        getHotSlotGames,
        hotSlotGames,
        updateUsersLive,
        getGameProviders,
        getGameTypes,
        gameTypes,
    }
})
